/**
 * Date and time formatting utilities
 */

/**
 * Format a date as a relative time string (e.g., "2 hours ago")
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted relative time string
 */
export function formatTimeAgo(date) {
    if (!date) return 'N/A';

    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) {
        return `${diffInSeconds} second${diffInSeconds !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 604800) {
        const days = Math.floor(diffInSeconds / 86400);
        return `${days} day${days !== 1 ? 's' : ''} ago`;
    } else {
        const weeks = Math.floor(diffInSeconds / 604800);
        return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
    }
}

/**
 * Number and Currency Formatters
 */

/**
 * Format number to maximum decimal places
 * @param {number} value - Number to format
 * @param {number} decimals - Maximum decimal places (default 2)
 * @returns {string} Formatted number
 */
export function formatNumber(value, decimals = 2) {
    if (value === null || value === undefined || isNaN(value)) return '–';
    if (!Number.isFinite(value)) return '–';

    // Round to specified decimals and remove trailing zeros
    return Number(value.toFixed(decimals)).toString();
}

/**
 * Format currency (EUR)
 * @param {number} value - Amount in EUR
 * @param {boolean} compact - Use compact notation (K, M)
 * @returns {string} Formatted currency
 */
export function formatCurrency(value, compact = false) {
    if (value === null || value === undefined || isNaN(value)) return '–';

    if (compact && value >= 1000000) {
        return `€${(value / 1000000).toFixed(1)}M`;
    } else if (compact && value >= 1000) {
        return `€${(value / 1000).toFixed(0)}K`;
    }

    return new Intl.NumberFormat('pt-PT', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0
    }).format(value);
}

/**
 * Format area (square meters)
 * @param {number} value - Area in m²
 * @returns {string} Formatted area with unit
 */
export function formatArea(value) {
    if (value === null || value === undefined || isNaN(value)) return '–';
    return `${formatNumber(value, 2)}m²`;
}

/**
 * Format price per square meter
 * @param {number} value - Price per m²
 * @returns {string} Formatted price/m²
 */
export function formatPricePerM2(value) {
    if (value === null || value === undefined || isNaN(value)) return '–';
    return `€${formatNumber(value, 0)}/m²`;
}

/**
 * Format ratio (e.g., bathroom ratio)
 * @param {number} value - Ratio value
 * @returns {string} Formatted ratio
 */
export function formatRatio(value) {
    if (value === null || value === undefined || isNaN(value)) return '–';
    return formatNumber(value, 2);
}
